(function () {
  'use strict';

  function focusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (element) {
      return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
    });
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;
    var focusable = focusableElements(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  var predictiveSearchPromise = null;

  function loadPredictiveSearch(header) {
    if (!header || !header.isConnected || !header.dataset.predictiveSearchScript) return Promise.resolve(null);
    if (window.CustoGiftsPredictiveSearch) {
      window.CustoGiftsPredictiveSearch.init(header);
      return Promise.resolve(window.CustoGiftsPredictiveSearch);
    }
    if (!predictiveSearchPromise) {
      predictiveSearchPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = header.dataset.predictiveSearchScript;
        script.async = true;
        script.dataset.cgPredictiveBundle = 'true';
        script.addEventListener('load', function () {
          if (window.CustoGiftsPredictiveSearch) resolve(window.CustoGiftsPredictiveSearch);
          else reject(new Error('Predictive search did not initialize'));
        }, { once: true });
        script.addEventListener('error', function () {
          reject(new Error('Predictive search could not be loaded'));
        }, { once: true });
        document.head.appendChild(script);
      }).catch(function (error) {
        predictiveSearchPromise = null;
        throw error;
      });
    }
    return predictiveSearchPromise.then(function (api) {
      if (header.isConnected) api.init(header);
      return api;
    });
  }

  function initHeader(header) {
    if (!header || header.dataset.headerReady === 'true') return;
    header.dataset.headerReady = 'true';
    var listenerController = new AbortController();
    var listenerSignal = listenerController.signal;
    header._cgHeaderAbort = listenerController;

    var menu = header.querySelector('[data-mobile-menu]');
    var menuToggle = header.querySelector('[data-mobile-menu-toggle]');
    var menuClose = header.querySelector('[data-mobile-menu-close]');
    var searchDialog = header.querySelector('[data-search-dialog]');
    var searchToggle = header.querySelector('[data-search-dialog-toggle]');
    var searchClose = header.querySelector('[data-search-dialog-close]');
    var navDetails = Array.from(header.querySelectorAll('[data-nav-details]'));
    var mobileNavDetails = Array.from(header.querySelectorAll('[data-mobile-nav-details]'));
    var hoverNavQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    var navPrimary = header.querySelector('.header-nav__primary');
    var navTriggers = navPrimary
      ? Array.from(navPrimary.querySelectorAll('[data-nav-trigger]'))
      : [];
    var lastFocused = null;
    var predictiveRoots = Array.from(header.querySelectorAll('[data-predictive-search]'));

    function requestPredictiveSearch() {
      loadPredictiveSearch(header).catch(function () {});
    }

    predictiveRoots.forEach(function (root) {
      root.addEventListener('pointerenter', requestPredictiveSearch, { passive: true, signal: listenerSignal });
      root.addEventListener('focusin', requestPredictiveSearch, { signal: listenerSignal });
      root.addEventListener('input', function (event) {
        if (window.CustoGiftsPredictiveSearch) return;
        var input = event.target;
        loadPredictiveSearch(header).then(function () {
          if (input && input.isConnected) input.dispatchEvent(new Event('input', { bubbles: true }));
        }).catch(function () {});
      }, { signal: listenerSignal });
    });

    function hasOpenOverlay() {
      return (menu && !menu.hidden) || (searchDialog && !searchDialog.hidden);
    }

    function syncBodyLock() {
      var overlayOpen = hasOpenOverlay();
      if (!window.CustoGiftsOverlay) document.body.classList.toggle('cg-overlay-open', overlayOpen);
      header.classList.toggle('site-header--overlay-open', overlayOpen);
    }

    function reveal(panel) {
      if (!panel) return;
      window.clearTimeout(panel._cgCloseTimer);
      panel.hidden = false;
      window.requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
      if (window.CustoGiftsOverlay) window.CustoGiftsOverlay.activate(panel);
      syncBodyLock();
    }

    function conceal(panel, trigger, restoreFocus) {
      if (!panel || panel.hidden) return;
      panel.classList.remove('is-open');
      if (window.CustoGiftsOverlay) window.CustoGiftsOverlay.deactivate(panel);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      panel._cgCloseTimer = window.setTimeout(function () {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
        syncBodyLock();
      }, 220);
      if (restoreFocus && lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function openMenu() {
      if (!menu) return;
      if (searchDialog && !searchDialog.hidden) conceal(searchDialog, searchToggle, false);
      lastFocused = document.activeElement;
      reveal(menu);
      menuToggle.setAttribute('aria-expanded', 'true');
      var target = menu.querySelector('[data-mobile-menu-close]');
      if (target) window.setTimeout(function () { target.focus(); }, 30);
    }

    function closeMenu(restoreFocus) {
      conceal(menu, menuToggle, restoreFocus !== false);
    }

    function openSearch() {
      if (!searchDialog) return;
      requestPredictiveSearch();
      if (menu && !menu.hidden) conceal(menu, menuToggle, false);
      lastFocused = document.activeElement;
      reveal(searchDialog);
      searchToggle.setAttribute('aria-expanded', 'true');
      var input = searchDialog.querySelector('input[type="search"]');
      if (input) window.setTimeout(function () { input.focus(); }, 30);
    }

    function closeSearch(restoreFocus) {
      conceal(searchDialog, searchToggle, restoreFocus !== false);
    }

    if (menuToggle) {
      menuToggle.addEventListener('click', function () {
        if (menu && !menu.hidden) closeMenu();
        else openMenu();
      }, { signal: listenerSignal });
    }
    if (menuClose) menuClose.addEventListener('click', function () { closeMenu(); }, { signal: listenerSignal });

    if (searchToggle) {
      searchToggle.addEventListener('click', function () {
        if (searchDialog && !searchDialog.hidden) closeSearch();
        else openSearch();
      }, { signal: listenerSignal });
    }
    if (searchClose) searchClose.addEventListener('click', function () { closeSearch(); }, { signal: listenerSignal });

    header.querySelectorAll('[data-overlay-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (menu && !menu.hidden) closeMenu();
        if (searchDialog && !searchDialog.hidden) closeSearch();
      }, { signal: listenerSignal });
    });

    function syncNavDetails(details) {
      var summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    }

    function closeNavDetails(details) {
      window.clearTimeout(details._cgOpenTimer);
      window.clearTimeout(details._cgCloseTimer);
      delete details.dataset.navPinned;
      details.removeAttribute('open');
      syncNavDetails(details);
    }

    function closeOtherNavDetails(activeDetails) {
      navDetails.forEach(function (other) {
        if (other !== activeDetails) closeNavDetails(other);
      });
    }

    function openNavDetails(details, pinned) {
      window.clearTimeout(details._cgOpenTimer);
      window.clearTimeout(details._cgCloseTimer);
      closeOtherNavDetails(details);
      details.setAttribute('open', '');
      if (pinned) details.dataset.navPinned = 'true';
      syncNavDetails(details);
    }

    function queueNavClose(details) {
      window.clearTimeout(details._cgOpenTimer);
      window.clearTimeout(details._cgCloseTimer);
      if (details.dataset.navPinned === 'true') return;
      details._cgCloseTimer = window.setTimeout(function () {
        if (!details.matches(':hover') && !details.contains(document.activeElement)) closeNavDetails(details);
      }, 210);
    }

    navDetails.forEach(function (details) {
      var summary = details.querySelector(':scope > summary');
      details.addEventListener('toggle', function () {
        syncNavDetails(details);
        if (details.open) closeOtherNavDetails(details);
      }, { signal: listenerSignal });

      details.addEventListener('pointerenter', function () {
        if (!hoverNavQuery.matches) return;
        window.clearTimeout(details._cgCloseTimer);
        details._cgOpenTimer = window.setTimeout(function () { openNavDetails(details, false); }, 100);
      }, { signal: listenerSignal });

      details.addEventListener('pointerleave', function () {
        if (hoverNavQuery.matches) queueNavClose(details);
      }, { signal: listenerSignal });

      details.addEventListener('focusin', function () {
        openNavDetails(details, details.dataset.navPinned === 'true');
      }, { signal: listenerSignal });

      details.addEventListener('focusout', function () {
        window.setTimeout(function () {
          if (!details.contains(document.activeElement)) closeNavDetails(details);
        }, 0);
      }, { signal: listenerSignal });

      if (summary) summary.addEventListener('click', function (event) {
        if (!hoverNavQuery.matches) return;
        event.preventDefault();
        if (details.open && details.dataset.navPinned === 'true') closeNavDetails(details);
        else openNavDetails(details, true);
      }, { signal: listenerSignal });
    });

    mobileNavDetails.forEach(function (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        mobileNavDetails.forEach(function (other) {
          if (other !== details) other.removeAttribute('open');
        });
      }, { signal: listenerSignal });
    });

    if (navPrimary && navTriggers.length) {
      navPrimary.addEventListener('keydown', function (event) {
        var trigger = event.target.closest && event.target.closest('[data-nav-trigger]');
        if (!trigger || !navPrimary.contains(trigger)) return;

        var triggerIndex = navTriggers.indexOf(trigger);
        var nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (triggerIndex + 1) % navTriggers.length;
        if (event.key === 'ArrowLeft') nextIndex = (triggerIndex - 1 + navTriggers.length) % navTriggers.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = navTriggers.length - 1;

        if (nextIndex !== null) {
          event.preventDefault();
          navTriggers[nextIndex].focus();
          return;
        }

        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && trigger.tagName === 'SUMMARY') {
          var owner = trigger.parentElement;
          var panel = owner && owner.querySelector(':scope > [data-mega-menu]');
          if (!owner || !panel) return;
          event.preventDefault();
          openNavDetails(owner, false);
          var panelLinks = focusableElements(panel);
          var panelTarget = event.key === 'ArrowUp'
            ? panelLinks[panelLinks.length - 1]
            : panelLinks[0];
          if (panelTarget) panelTarget.focus();
        }
      }, { signal: listenerSignal });

      navPrimary.addEventListener('focusin', function (event) {
        var trigger = event.target.closest && event.target.closest('[data-nav-trigger]');
        if (!trigger) return;
        navDetails.forEach(function (details) {
          if (!details.contains(trigger)) closeNavDetails(details);
        });
      }, { signal: listenerSignal });
    }

    document.addEventListener('shopify:block:select', function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target || !header.contains(target)) return;
      var panel = target.closest('[data-mega-menu]');
      var details = panel && panel.closest('[data-nav-details]');
      if (!details) return;

      closeOtherNavDetails(details);
      openNavDetails(details, true);
    }, { signal: listenerSignal });

    document.addEventListener('click', function (event) {
      var activeDetails = event.target.closest && event.target.closest('[data-nav-details]');
      if (!activeDetails) navDetails.forEach(closeNavDetails);
    }, { signal: listenerSignal });

    header.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        var openNav = navDetails.find(function (details) { return details.open; });
        if (openNav && openNav.contains(document.activeElement)) {
          var summary = openNav.querySelector('summary');
          if (summary) summary.focus();
        }
        navDetails.forEach(closeNavDetails);
        if (menu && !menu.hidden) closeMenu();
        if (searchDialog && !searchDialog.hidden) closeSearch();
      }
      if (menu && !menu.hidden) trapFocus(event, menu);
      if (searchDialog && !searchDialog.hidden) trapFocus(event, searchDialog);
    }, { signal: listenerSignal });

    var scrollFrame = null;
    var scrolledState = null;
    var updateScrolledState = function () {
      var nextState = window.scrollY > 10;
      if (nextState === scrolledState) return;
      scrolledState = nextState;
      header.classList.toggle('is-scrolled', nextState);
    };
    var requestScrolledState = function () {
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(function () {
        scrollFrame = null;
        updateScrolledState();
      });
    };
    updateScrolledState();
    window.addEventListener('scroll', requestScrolledState, { passive: true, signal: listenerSignal });
    listenerSignal.addEventListener('abort', function () {
      navDetails.forEach(function (details) {
        window.clearTimeout(details._cgOpenTimer);
        window.clearTimeout(details._cgCloseTimer);
      });
      if (scrollFrame !== null) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = null;
      }
    }, { once: true });
  }

  function initAll(scope) {
    var root = scope || document;
    if (root.matches && root.matches('[data-site-header]')) initHeader(root);
    root.querySelectorAll('[data-site-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var header = event.target.matches && event.target.matches('[data-site-header]')
      ? event.target
      : event.target.querySelector && event.target.querySelector('[data-site-header]');
    if (!header) return;
    if (window.CustoGiftsOverlay) {
      var menu = header.querySelector('[data-mobile-menu]');
      var searchDialog = header.querySelector('[data-search-dialog]');
      if (menu) window.CustoGiftsOverlay.deactivate(menu);
      if (searchDialog) window.CustoGiftsOverlay.deactivate(searchDialog);
    }
    if (header._cgHeaderAbort) header._cgHeaderAbort.abort();
  });
})();
